/**
 * CareLogix™ ORACLE — AdminOne Live LLM Engine™ (additive, upgrade-only).
 *
 * Truthful multi-provider status board + live streaming test console.
 * Backend-only keys — this page never sees or requests any secret value.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

const BACKEND =
  (typeof process !== "undefined" && process.env && process.env.REACT_APP_BACKEND_URL) || "";
const API = `${BACKEND}/api/oracle/live-llm`;

const STATUS_COLORS = {
  LIVE: { bg: "rgba(122,200,96,.10)", bd: "rgba(122,200,96,.5)", fg: "#8fdc76" },
  AWAITING_PROVIDER_KEY: { bg: "rgba(232,160,32,.10)", bd: "rgba(232,160,32,.5)", fg: "#e8a020" },
  PARTNER_NOT_CONNECTED: { bg: "rgba(120,130,150,.10)", bd: "rgba(120,130,150,.45)", fg: "#98a2b6" },
  CANNOT_VERIFY: { bg: "rgba(200,74,58,.10)", bd: "rgba(200,74,58,.5)", fg: "#e07a6a" },
};

const S = {
  page: { padding: "24px 28px 80px", background: "linear-gradient(160deg,#050b09 0%,#06231b 60%,#050b09 100%)", color: "#dfe3eb", minHeight: "100vh", fontFamily: "'Figtree','Inter',system-ui,sans-serif" },
  eyebrow: { color: "#c5a059", fontFamily: "'Space Mono',monospace", fontSize: 11, letterSpacing: "0.18em", marginBottom: 4 },
  title: { color: "#fde68a", margin: 0, fontSize: 26, fontFamily: "'Cinzel',serif", letterSpacing: "0.02em" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 14, marginTop: 18 },
  card: { border: "1px solid rgba(197,160,89,.22)", borderRadius: 14, padding: "16px 18px", background: "rgba(6,26,20,.65)" },
  pill: (st) => ({ display: "inline-block", padding: "3px 10px", borderRadius: 999, fontSize: 11, fontFamily: "'Space Mono',monospace", letterSpacing: "0.08em", background: (STATUS_COLORS[st] || STATUS_COLORS.PARTNER_NOT_CONNECTED).bg, border: `1px solid ${(STATUS_COLORS[st] || STATUS_COLORS.PARTNER_NOT_CONNECTED).bd}`, color: (STATUS_COLORS[st] || STATUS_COLORS.PARTNER_NOT_CONNECTED).fg }),
  sectionTitle: { color: "#fde68a", fontFamily: "'Cinzel',serif", fontSize: 18, margin: "28px 0 4px" },
  console: { marginTop: 12, border: "1px solid rgba(197,160,89,.22)", borderRadius: 14, padding: 16, background: "rgba(6,26,20,.65)" },
  input: { flex: 1, padding: "10px 12px", borderRadius: 10, border: "1px solid rgba(197,160,89,.3)", background: "#04140d", color: "#e8ecf4", fontSize: 14 },
  select: { padding: "10px 12px", borderRadius: 10, border: "1px solid rgba(197,160,89,.3)", background: "#04140d", color: "#e8ecf4", fontSize: 14 },
  btn: { padding: "10px 18px", borderRadius: 10, border: "1px solid rgba(245,200,66,.5)", background: "linear-gradient(180deg,#f5c842,#c99a1f)", color: "#1b1304", fontWeight: 700, cursor: "pointer" },
  out: { marginTop: 14, whiteSpace: "pre-wrap", minHeight: 60, padding: 14, borderRadius: 10, background: "#04140d", border: "1px solid rgba(197,160,89,.18)", fontFamily: "'Space Mono',monospace", fontSize: 13.5, lineHeight: 1.6 },
  meta: { fontSize: 11, color: "#98a2b6", fontFamily: "'Space Mono',monospace", marginTop: 8 },
  note: { fontSize: 12, color: "#98a2b6", marginTop: 8 },
};

export default function AdminOneLiveLLMEngine() {
  const [reg, setReg] = useState(null);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState("");
  const [mode, setMode] = useState("fast");
  const [out, setOut] = useState("");
  const [metaLine, setMetaLine] = useState("");
  const [running, setRunning] = useState(false);
  const sessionRef = useRef(`adminone-llm-${Date.now()}`);

  const loadStatus = useCallback(() => {
    fetch(`${API}/status`)
      .then((r) => r.json())
      .then(setReg)
      .catch(() => setErr("Failed to load Live LLM status."));
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const runStream = useCallback(async () => {
    if (!msg.trim() || running) return;
    setRunning(true);
    setOut("");
    setMetaLine("");
    try {
      const resp = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, session_id: sessionRef.current, mode }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const line = p.replace(/^data:\s*/, "").trim();
          if (!line) continue;
          try {
            const ev = JSON.parse(line);
            if (ev.type === "meta") setMetaLine(`${ev.provider} · ${ev.model} · ${ev.mode}`);
            else if (ev.type === "delta") setOut((o) => o + ev.content);
            else if (ev.type === "degraded") setOut((o) => o + ev.content);
            else if (ev.type === "done") setMetaLine((m) => `${m}  ·  ${ev.provider_status.toUpperCase()}${ev.failover_used ? " · FAILOVER" : ""}`);
          } catch { /* ignore partial */ }
        }
      }
    } catch {
      setOut("Live LLM stream unavailable — offline intelligence mode. No output was fabricated.");
    } finally {
      setRunning(false);
    }
  }, [msg, mode, running]);

  return (
    <div style={S.page} data-testid="adminone-live-llm-page">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={S.eyebrow}>ORACLE LIVE OPERATIONS UPGRADE · PHASE 2</div>
          <h1 style={S.title}>Live LLM Engine™</h1>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button type="button" style={{ ...S.btn, background: "transparent", color: "#c5a059", border: "1px solid rgba(197,160,89,.4)" }} onClick={loadStatus} data-testid="live-llm-refresh">Refresh</button>
          <Link to="/adminone/telemetry" style={{ ...S.btn, textDecoration: "none", background: "transparent", color: "#c5a059", border: "1px solid rgba(197,160,89,.4)" }}>← Telemetry</Link>
        </div>
      </div>

      {err && <div style={{ ...S.note, color: "#e07a6a" }} data-testid="live-llm-error">{err}</div>}

      {reg && (
        <>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 14 }} data-testid="live-llm-summary">
            <span style={S.pill(reg.degraded ? "CANNOT_VERIFY" : "LIVE")}>{reg.degraded ? "ENGINE DEGRADED" : `ENGINE LIVE · ${reg.live_count} PROVIDERS`}</span>
            <span style={S.pill(reg.universal_key_configured ? "LIVE" : "AWAITING_PROVIDER_KEY")}>UNIVERSAL KEY {reg.universal_key_configured ? "READY" : "MISSING"}</span>
          </div>

          <div style={S.sectionTitle}>Providers</div>
          <div style={S.grid}>
            {reg.providers.map((p) => (
              <div key={p.provider} style={S.card} data-testid={`live-llm-provider-${p.provider}`}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <strong style={{ color: "#fde68a", textTransform: "capitalize" }}>{p.provider.replace("_", " ")}</strong>
                  <span style={S.pill(p.status)}>{p.status}</span>
                </div>
                <div style={S.note}>{p.note}</div>
                <div style={S.meta}>{p.routable ? "ROUTABLE" : "NOT ROUTABLE"}</div>
              </div>
            ))}
          </div>

          <div style={S.sectionTitle}>Modes</div>
          <div style={S.grid}>
            {Object.entries(reg.modes).map(([m, cfg]) => (
              <div key={m} style={S.card} data-testid={`live-llm-mode-${m}`}>
                <strong style={{ color: "#fde68a", textTransform: "uppercase" }}>{m}</strong>
                <div style={S.meta}>{cfg.provider} · {cfg.model}</div>
              </div>
            ))}
          </div>

          <div style={S.sectionTitle}>Live Streaming Test Console</div>
          <div style={S.console} data-testid="live-llm-console">
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <select style={S.select} value={mode} onChange={(e) => setMode(e.target.value)} data-testid="live-llm-mode-select">
                <option value="fast">Fast</option>
                <option value="reasoning">Reasoning</option>
                <option value="deep">Deep Research</option>
              </select>
              <input style={S.input} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Ask the Live LLM Engine…" data-testid="live-llm-input" onKeyDown={(e) => { if (e.key === "Enter") runStream(); }} />
              <button type="button" style={{ ...S.btn, opacity: running ? 0.6 : 1 }} onClick={runStream} disabled={running} data-testid="live-llm-send">{running ? "Streaming…" : "Send"}</button>
            </div>
            {metaLine && <div style={S.meta} data-testid="live-llm-meta">{metaLine}</div>}
            <div style={S.out} data-testid="live-llm-output">{out || "Response streams here token-by-token."}</div>
            <div style={S.note}>{reg.compliance}</div>
          </div>
        </>
      )}
    </div>
  );
}
